import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAccount } from "@/contexts/AccountContext";
import { supabase } from "@/lib/supabase";
import { oxpayAPI } from "@/lib/oxpay";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CreditCard,
  Lock,
  History,
  Settings,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  WalletCards,
  Calendar,
  CreditCardIcon,
} from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import CardSwiper from "@/components/CardSwiper";

export default function MyCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { oxpayUserId } = useAccount();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardStatus, setCardStatus] = useState("inactive"); // active, locked, inactive, expired
  const [selectedCard, setSelectedCard] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);

  // Fetch card data from active_cards table
  useEffect(() => {
    const fetchCardData = async () => {
      if (!user?.email) return;

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("active_cards")
          .select("*")
          .eq("email", user.email)
          .order("created_at", { ascending: true });

        if (error) {
          console.error("Error fetching card:", error);
          return;
        }

        if (data && data.length > 0) {
          setCardData(data);
          setCardStatus(data[0].is_active ? "active" : "inactive");
        }
      } catch (err) {
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCardData();
  }, [user]);

  // Fetch wallet balance when oxpayUserId is available
  useEffect(() => {
    const fetchWalletInfo = async () => {
      if (!oxpayUserId) return;

      try {
        const response = await oxpayAPI.getWallet(oxpayUserId);

        if (response?.headers?.status_code === "error") {
          throw new Error(
            response?.body?.description || "Failed to fetch wallet info"
          );
        }

        setWalletInfo(response?.body || null);
      } catch (err) {
        console.error("Error fetching wallet info:", err);
      }
    };

    fetchWalletInfo();
  }, [oxpayUserId]);

  const quickActions = [
    {
      icon: History,
      label: "View Transaction History",
      description:
        selectedCard && selectedCard.isActive
          ? "Check your card usage and spending"
          : "Activate your card to view transactions",
      action: () => {
        if (selectedCard && selectedCard.isActive) {
          navigate("/history", {
            state: {
              selectedCardId: selectedCard.id,
              selectedCardNumber: selectedCard.number,
            },
          });
        }
      },
      variant: "outline",
      disabled: !selectedCard || !selectedCard.isActive,
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "locked":
        return <Lock className="w-4 h-4 text-yellow-500" />;
      case "expired":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "inactive":
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-500";
      case "locked":
        return "text-yellow-500";
      case "expired":
        return "text-red-500";
      case "inactive":
        return "text-gray-500";
      default:
        return "text-gray-500";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header showBack={true} onBackClick={() => navigate("/home")} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
        <BottomBar />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/home")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Card Status Banner */}
        {/* {selectedCard && selectedCard.isLocked && (
          <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Your card is temporarily locked. Unlock it to make transactions.
              </p>
            </div>
          </div>
        )} */}

        {/* {selectedCard && !selectedCard.isActive && (
          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-gray-600 dark:text-gray-500" />
              <p className="text-sm text-gray-800 dark:text-gray-200">
                Your card is not activated yet. Please activate your card to use
                it.
              </p>
            </div>
          </div>
        )} */}

        {/* Card Swiper Section */}
        <div className="mt-4">
          <CardSwiper
            showCardSelect={true}
            showLockStolen={true}
            showActivation={cardStatus !== "active"}
            cardsData={
              cardData && cardData.length > 0
                ? cardData.map((card) => ({
                    id: card.id,
                    oxpay_card_id: card.oxpay_card_id || "",
                    card_number: card.card_number || "",
                    card_balance: walletInfo?.funds?.available?.amount || "-",
                    card_type: card.card_type || "prepaid",
                    card_status: card.is_active ? "active" : "inactive",
                    card_brand: card.card_brand || "mastercard",
                    card_expiry: card.card_expiry,
                    payment_status: "completed",
                    fellas_order_id: card.sid || card.id,
                    is_active: card.is_active || false,
                  }))
                : []
            }
            userEmail={user?.email || ""}
            onCardChange={(card) => setSelectedCard(card)}
          />
        </div>

        {/* Quick Actions */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-3">Quick Actions</h2>
          <div className="grid gap-3">
            {quickActions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant}
                className={`justify-between h-auto py-3 ${
                  action.disabled
                    ? "bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed"
                    : ""
                }`}
                onClick={action.action}
                disabled={action.disabled}
              >
                <div className="flex items-center">
                  <action.icon
                    className={`w-5 h-5 mr-3 ${
                      action.disabled ? "text-gray-400" : ""
                    }`}
                  />
                  <div className="text-left">
                    <div
                      className={`font-medium ${
                        action.disabled ? "text-gray-400" : ""
                      }`}
                    >
                      {action.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {action.description}
                    </div>
                  </div>
                </div>
                <span
                  className={`text-xl ${
                    action.disabled ? "text-gray-400" : ""
                  }`}
                >
                  〉
                </span>
              </Button>
            ))}
          </div>
        </div>

        {/* Transaction History Button */}
        {/* <div className="mt-6">
          <h2 className="text-lg font-semibold mb-3">Transactions</h2>
          <Button
            variant="outline"
            className={`w-full h-auto py-4 justify-between ${
              !selectedCard || !selectedCard.isActive
                ? "bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed"
                : ""
            }`}
            onClick={() =>
              selectedCard &&
              selectedCard.isActive &&
              navigate("/history", {
                state: {
                  selectedCardId: selectedCard.id,
                  selectedCardNumber: selectedCard.number,
                },
              })
            }
            disabled={!selectedCard || !selectedCard.isActive}
          >
            <div className="text-left">
              <p
                className={`font-medium ${
                  !selectedCard || !selectedCard.isActive ? "text-gray-400" : ""
                }`}
              >
                View Transaction History
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {!selectedCard || !selectedCard.isActive
                  ? "Activate your card to view transactions"
                  : "Check your card usage and spending"}
              </p>
            </div>
            <span
              className={`text-xl ${
                !selectedCard || !selectedCard.isActive ? "text-gray-400" : ""
              }`}
            >
              〉
            </span>
          </Button>
        </div> */}
      </div>

      <BottomBar />
    </div>
  );
}
